import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Editor, useCurrentEditor } from "@tiptap/react";
import { UseFormReturn } from "react-hook-form";
import { FormContainer } from "react-hook-form-mui";

import ButtonLoading from "../../Button/ButtonLoading";
import LegalbirdTipTapEditor from "../../LbEditor/LegalbirdTipTapEditor";
import LegalbirdTipTapEditorProvider from "../../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { CasePdfGeneratorFormValues } from "../../../types/CasePdfGenerator";

type PdfGeneratorDraftFeedbackModalProps = {
  open: boolean;
  formContext: UseFormReturn<CasePdfGeneratorFormValues>;
  onClose: () => void;
  onSubmit: () => Promise<boolean> | boolean;
  onInquiryTextChange?: (inquiryText: string) => void;
};

const defaultInquiryText =
  "<p>Wir bitten um Ihre Rückmeldung zu einem Schriftsatz. Den Entwurf finden Sie im Anhang.</p>\n" +
  "<p>Bitte überprüfen Sie das Schreiben auf inhaltliche Richtigkeit und teilen Sie uns so bald wie möglich über das untenstehende Textfeld mit, ob das Schreiben aus Ihrer Sicht inhaltlich korrekt ist oder ob Änderungen erforderlich sind.</p>\n" +
  "<p>Vielen Dank für Ihre Unterstützung!</p>";

const PdfGeneratorDraftFeedbackModal = ({ onInquiryTextChange, ...props }: PdfGeneratorDraftFeedbackModalProps) => {
  if (!props.open) {
    return null;
  }

  return (
    <LegalbirdTipTapEditorProvider
      onUpdate={({ editor }: { editor: Editor }) => onInquiryTextChange?.(getEditorHtml(editor) ?? "")}
    >
      <PdfGeneratorDraftFeedbackModalInner {...props} />
    </LegalbirdTipTapEditorProvider>
  );
};

const PdfGeneratorDraftFeedbackModalInner = ({
  open,
  formContext,
  onClose,
  onSubmit,
}: PdfGeneratorDraftFeedbackModalProps) => {
  const { editor } = useCurrentEditor();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!editor || !open) {
      console.log("dont set content");
      return;
    }

    console.log("set content");
    editor.commands.setContent(defaultInquiryText, true);
    editor.commands.focus("start");
  }, [editor, open]);

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const isSuccessful = await onSubmit();

      if (isSuccessful) {
        onClose();
      }
    } catch (error) {
      // Error handling should happen in the parent submit handler.
      // The modal stays open.
    } finally {
      setIsLoading(false);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <LegalbirdIoModal
      open={open}
      handleClose={handleClose}
      disableBackdropClick
      title="Entwurf an Mandantschaft schicken"
      submitButton={
        <ButtonLoading
          variant="contained"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={formContext.handleSubmit(handleSubmit)}
        >
          Entwurf abschicken
        </ButtonLoading>
      }
    >
      <FormContainer formContext={formContext}>
        <Grid container spacing={2}>
          <Grid size={12}>
            Es wird ein Dokument mit den Daten dieses Entwurfs generiert und per Anfrage an die Mandantschaft mit der
            Bitte um Feedback gesendet.
          </Grid>

          <Grid size={12}>
            <LegalbirdTipTapEditor name="inquiryText" />
          </Grid>
        </Grid>
      </FormContainer>
    </LegalbirdIoModal>
  );
};

export default PdfGeneratorDraftFeedbackModal;
